import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { deckData } from "../../Services/deckData";
import {
  streamTarotReading,
  checkClientRateLimit,
  logSpreadReading,
  logSpreadFocus,
  RateLimitError,
} from "../../Services/aiReading";
import { cardMeanings } from "../../Services/cardMeanings";
import StaticCardMeaning from "../StaticCardMeaning/StaticCardMeaning";
import { useUtilImages } from "../../Services/Hooks/useUtilImages";
import { useLanguage } from "../../Services/Context/LanguageContext";
import { useSpeechContext } from "../../Services/Context/SpeechContext";
import type { SpreadType } from "../../config/spreadConfigs";
import { SPREAD_CONFIGS } from "../../config/spreadConfigs";
import "animate.css";
import "./SpreadReading.css";

export interface SpreadCard {
  name: string;
  isReversed: boolean;
  image: string;
}

interface SpreadReadingProps {
  spreadType: SpreadType;
  question: string;
  isReversedCardsEnabled: boolean;
  onDrawAgain: () => void;
  aiEnabled: boolean;
}

const CARD_ANIMATIONS = [
  "animate__zoomInLeft",
  "animate__zoomInUp",
  "animate__zoomInRight",
];

export default function SpreadReading({
  spreadType,
  question,
  isReversedCardsEnabled,
  onDrawAgain,
  aiEnabled,
}: SpreadReadingProps) {
  const { translate, language } = useLanguage();
  const { cardBack } = useUtilImages();
  const { speak } = useSpeechContext();

  const config = SPREAD_CONFIGS[spreadType];
  const positions = config.positions;

  // Draw 3 unique cards once on mount (null-guard is safe across Strict Mode re-renders)
  const cardsRef = useRef<SpreadCard[] | null>(null);
  if (!cardsRef.current) {
    const shuffled = [...deckData].sort(() => Math.random() - 0.5);
    cardsRef.current = shuffled.slice(0, 3).map((c) => ({
      name: c.cardName,
      isReversed: isReversedCardsEnabled && Math.random() < 0.5,
      image: c.image,
    }));
  }
  const cards = cardsRef.current;

  const [flipped, setFlipped] = useState<[boolean, boolean, boolean]>([
    false,
    false,
    false,
  ]);
  const [readings, setReadings] = useState<[string, string, string]>([
    "",
    "",
    "",
  ]);
  const [loading, setLoading] = useState<[boolean, boolean, boolean]>([
    false,
    false,
    false,
  ]);
  const [errors, setErrors] = useState<
    [string | null, string | null, string | null]
  >([null, null, null]);

  const [focusCard, setFocusCard] = useState<SpreadCard | null>(null);
  const [focusPhase, setFocusPhase] = useState<"drawn" | "flipped" | null>(
    null,
  );
  const [focusReading, setFocusReading] = useState("");
  const [focusLoading, setFocusLoading] = useState(false);
  const [focusError, setFocusError] = useState<string | null>(null);

  const controllersRef = useRef<(AbortController | null)[]>([null, null, null]);
  const focusControllerRef = useRef<AbortController | null>(null);
  const rateLimitCheckedRef = useRef(false);
  const languageRef = useRef(language);
  languageRef.current = language;

  // Speak each card's AI reading when its stream completes (loading true → false)
  const prevLoadingRef = useRef<[boolean, boolean, boolean]>([
    false,
    false,
    false,
  ]);
  useEffect(() => {
    loading.forEach((isLoading, i) => {
      const idx = i as 0 | 1 | 2;
      if (!isLoading && prevLoadingRef.current[idx] && readings[idx]) {
        speak(readings[idx]);
      }
    });
    prevLoadingRef.current = [...loading] as [boolean, boolean, boolean];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const prevFocusLoadingRef = useRef(false);
  useEffect(() => {
    if (!focusLoading && prevFocusLoadingRef.current && focusReading) {
      speak(focusReading);
    }
    prevFocusLoadingRef.current = focusLoading;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusLoading]);

  // Speak + log each card's static meaning when flipped with AI disabled (flipped false → true)
  const prevFlippedRef = useRef<[boolean, boolean, boolean]>([
    false,
    false,
    false,
  ]);
  useEffect(() => {
    if (aiEnabled) return;
    flipped.forEach((isFlipped, i) => {
      const idx = i as 0 | 1 | 2;
      if (isFlipped && !prevFlippedRef.current[idx]) {
        const card = cards[idx];
        const meaningData = cardMeanings[card.name];
        if (meaningData) {
          const meaning = card.isReversed
            ? meaningData.reversedMeaning
            : meaningData.meaning;
          speak(`${card.name}. ${meaning} ${meaningData.symbolism}`);
        }
      }
    });
    prevFlippedRef.current = [...flipped] as [boolean, boolean, boolean];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flipped]);

  const startStream = (
    idx: 0 | 1 | 2,
    previousSpreadCards?: Array<{ name: string; isReversed: boolean }>,
  ) => {
    const card = cards[idx];
    const controller = new AbortController();
    controllersRef.current[idx] = controller;

    (async () => {
      try {
        const gen = streamTarotReading(
          card.name,
          card.isReversed,
          question,
          undefined,
          controller.signal,
          undefined,
          languageRef.current,
          spreadType,
          positions[idx].key,
          previousSpreadCards,
          true, // skipRateLimit — checked once for the whole spread on first flip
        );
        for await (const chunk of gen) {
          setReadings((prev) => {
            const next = [...prev] as [string, string, string];
            next[idx] = prev[idx] + chunk;
            return next;
          });
        }
        setLoading((prev) => {
          const next = [...prev] as [boolean, boolean, boolean];
          next[idx] = false;
          return next;
        });
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") return;
        const msg =
          err instanceof RateLimitError
            ? err.message
            : translate("readingError");
        setErrors((prev) => {
          const next = [...prev] as [
            string | null,
            string | null,
            string | null,
          ];
          next[idx] = msg;
          return next;
        });
        setLoading((prev) => {
          const next = [...prev] as [boolean, boolean, boolean];
          next[idx] = false;
          return next;
        });
      }
    })();
  };

  const startFocusStream = (card: SpreadCard) => {
    const controller = new AbortController();
    focusControllerRef.current = controller;

    (async () => {
      try {
        const gen = streamTarotReading(
          card.name,
          card.isReversed,
          question,
          undefined,
          controller.signal,
          undefined,
          languageRef.current,
          spreadType,
          "focus",
          cards.map((c) => ({ name: c.name, isReversed: c.isReversed })),
          true, // skipRateLimit — part of the same reading session
        );
        for await (const chunk of gen) {
          setFocusReading((prev) => prev + chunk);
        }
        setFocusLoading(false);
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") return;
        const msg =
          err instanceof RateLimitError
            ? err.message
            : translate("readingError");
        setFocusError(msg);
        setFocusLoading(false);
      }
    })();
  };

  const handleDrawFocus = () => {
    const excludedNames = new Set(cards.map((c) => c.name));
    const available = deckData.filter((c) => !excludedNames.has(c.cardName));
    const pick = available[Math.floor(Math.random() * available.length)];
    setFocusCard({
      name: pick.cardName,
      isReversed: isReversedCardsEnabled && Math.random() < 0.5,
      image: pick.image,
    });
    setFocusPhase("drawn");
  };

  const handleFlipFocus = () => {
    if (focusPhase === "flipped" || !focusCard) return;
    setFocusPhase("flipped");
    logSpreadFocus(
      spreadType,
      { name: focusCard.name, isReversed: focusCard.isReversed },
      question,
    );
    if (aiEnabled) {
      setFocusLoading(true);
      startFocusStream(focusCard);
    } else {
      const meaningData = cardMeanings[focusCard.name];
      if (meaningData) {
        const meaning = focusCard.isReversed
          ? meaningData.reversedMeaning
          : meaningData.meaning;
        speak(`${focusCard.name}. ${meaning} ${meaningData.symbolism}`);
      }
    }
  };

  const handleFlip = (idx: 0 | 1 | 2) => {
    if (flipped[idx]) return;
    if (
      idx > 0 &&
      (!flipped[(idx - 1) as 0 | 1] ||
        (aiEnabled && loading[(idx - 1) as 0 | 1]))
    )
      return;

    // Check rate limit once on the first flip; block everything if exhausted
    if (idx === 0 && !rateLimitCheckedRef.current) {
      try {
        checkClientRateLimit();
        rateLimitCheckedRef.current = true;
      } catch (err) {
        if (err instanceof RateLimitError) {
          setErrors([err.message, err.message, err.message]);
          return;
        }
      }
    }

    setFlipped((prev) => {
      const next = [...prev] as [boolean, boolean, boolean];
      next[idx] = true;
      return next;
    });

    if (idx === 0) {
      const spreadCardsMap = Object.fromEntries(
        positions.map((pos, i) => [
          pos.key,
          { name: cards[i].name, isReversed: cards[i].isReversed },
        ]),
      );
      logSpreadReading(spreadType, spreadCardsMap, question, aiEnabled);
    }

    if (aiEnabled) {
      setLoading((prev) => {
        const next = [...prev] as [boolean, boolean, boolean];
        next[idx] = true;
        return next;
      });
      const previousSpreadCards =
        idx > 0
          ? cards
              .slice(0, idx)
              .map((c) => ({ name: c.name, isReversed: c.isReversed }))
          : undefined;
      startStream(idx, previousSpreadCards);
    }
  };

  useEffect(() => {
    const controllers = controllersRef.current;
    const focusController = focusControllerRef;
    return () => {
      controllers.forEach((c) => c?.abort());
      focusController.current?.abort();
    };
  }, []);

  const allFlipped = flipped.every(Boolean);

  return (
    <motion.div
      className="SpreadReading"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      {question && (
        <motion.p
          className="SpreadReading-question"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          "{question}"
        </motion.p>
      )}

      <div className="SpreadReading-grid">
        {positions.map((pos, idx) => {
          const card = cards[idx];
          const isFlipped = flipped[idx];
          const isDisabled =
            idx > 0 &&
            (!flipped[(idx - 1) as 0 | 1] ||
              (aiEnabled && loading[(idx - 1) as 0 | 1]));

          return (
            <div
              key={pos.key}
              className={`SpreadReading-column${isDisabled ? " SpreadReading-column--disabled" : ""}`}
            >
              <motion.span
                className="SpreadReading-position-label"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: idx * 0.15, duration: 0.6 }}
              >
                {translate(pos.labelKey)}
              </motion.span>

              <div
                className={`SpreadReading-card-scene animate__animated ${CARD_ANIMATIONS[idx]}${isFlipped ? " SpreadReading-card-scene--flipped" : ""}`}
                style={{ animationDelay: `${idx * 0.25}s` }}
                onClick={() => handleFlip(idx as 0 | 1 | 2)}
              >
                <motion.div
                  className="SpreadReading-card-inner"
                  animate={{ rotateY: isFlipped ? 180 : 0 }}
                  transition={{ duration: 0.85 }}
                >
                  <div className="SpreadReading-card-face SpreadReading-card-face--front">
                    <img
                      src={cardBack}
                      className="SpreadReading-card-img"
                      alt="card back"
                    />
                  </div>
                  <div className="SpreadReading-card-face SpreadReading-card-face--reveal">
                    <img
                      src={card.image}
                      className={`SpreadReading-card-img${card.isReversed ? " SpreadReading-card-img--reversed" : ""}`}
                      alt={card.name}
                    />
                  </div>
                </motion.div>
              </div>

              {isFlipped && (
                <motion.p
                  className="SpreadReading-card-name"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  {card.name}
                  {card.isReversed && (
                    <span className="SpreadReading-reversed">
                      {" "}
                      · {translate("reversed")}
                    </span>
                  )}
                </motion.p>
              )}

              {isFlipped && (
                <motion.div
                  className="SpreadReading-text"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  {aiEnabled ? (
                    errors[idx] ? (
                      <p className="SpreadReading-error">{errors[idx]}</p>
                    ) : loading[idx] && !readings[idx] ? (
                      <p className="SpreadReading-loading">✦</p>
                    ) : (
                      <p className="SpreadReading-reading-text">
                        {readings[idx]}
                      </p>
                    )
                  ) : (
                    <StaticCardMeaning
                      cardName={card.name}
                      isReversed={card.isReversed}
                    />
                  )}
                </motion.div>
              )}
            </div>
          );
        })}
      </div>

      {allFlipped && (
        <div className="SpreadReading-focus-section">
          {!focusCard && (
            <motion.button
              className="SpreadReading-focus-btn"
              onClick={handleDrawFocus}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
            >
              {translate("drawFocusCard")}
            </motion.button>
          )}

          {focusCard && (
            <div className="SpreadReading-focus-card-column">
              <motion.span
                className="SpreadReading-position-label"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
              >
                {translate("focus")}
              </motion.span>

              <div
                className={`SpreadReading-card-scene animate__animated animate__zoomInUp${focusPhase === "flipped" ? " SpreadReading-card-scene--flipped" : ""}`}
                onClick={handleFlipFocus}
              >
                <motion.div
                  className="SpreadReading-card-inner"
                  animate={{ rotateY: focusPhase === "flipped" ? 180 : 0 }}
                  transition={{ duration: 0.85 }}
                >
                  <div className="SpreadReading-card-face SpreadReading-card-face--front">
                    <img
                      src={cardBack}
                      className="SpreadReading-card-img"
                      alt="card back"
                    />
                  </div>
                  <div className="SpreadReading-card-face SpreadReading-card-face--reveal">
                    <img
                      src={focusCard.image}
                      className={`SpreadReading-card-img${focusCard.isReversed ? " SpreadReading-card-img--reversed" : ""}`}
                      alt={focusCard.name}
                    />
                  </div>
                </motion.div>
              </div>

              {focusPhase === "flipped" && (
                <motion.p
                  className="SpreadReading-card-name"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  {focusCard.name}
                  {focusCard.isReversed && (
                    <span className="SpreadReading-reversed">
                      {" "}
                      · {translate("reversed")}
                    </span>
                  )}
                </motion.p>
              )}

              {focusPhase === "flipped" && (
                <motion.div
                  className="SpreadReading-text"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  {aiEnabled ? (
                    focusError ? (
                      <p className="SpreadReading-error">{focusError}</p>
                    ) : focusLoading && !focusReading ? (
                      <p className="SpreadReading-loading">✦</p>
                    ) : (
                      <p className="SpreadReading-reading-text">
                        {focusReading}
                      </p>
                    )
                  ) : (
                    <StaticCardMeaning
                      cardName={focusCard.name}
                      isReversed={focusCard.isReversed}
                    />
                  )}
                </motion.div>
              )}
            </div>
          )}
        </div>
      )}

      {allFlipped && (
        <motion.button
          className="SpreadReading-draw-again"
          onClick={onDrawAgain}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
        >
          {translate("drawAgain")}
        </motion.button>
      )}
    </motion.div>
  );
}
